import React from "react";
import {Link} from "react-router-dom";
import {
    IconButton,
    makeStyles,
    Typography
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableContainer
    from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import {Delete, Edit} from "@material-ui/icons";
import {useRowSelect, useTable,} from "react-table";
import apiHelper from "../../../../util/ApiHelper";
import {
    CustomCheckbox,
    getKeyword,
    modal
} from "../../../../util";
import {useLoading} from "../../../../util/LoadingProvider";
import {toast} from "react-toastify";
import TablePagination
    from "@material-ui/core/TablePagination";
import TablePaginationActions
    from "@material-ui/core/TablePagination/TablePaginationActions";

const useStyles = makeStyles((theme) => ({
    textInput: {
        backgroundColor: "white",
        width: "100%",
        marginBottom: "12px",

        "& input": {
            fontSize: "14px",
        },
    },
    content: {
        "& .ck-editor__editable_inline": {
            minHeight: "200px",
        },
    },
    text: {
        textDecoration: "none",
        color: "#306F9D",
    },
    inactive: {
        color: "#999999",
    },
    buttonGroup: {
        marginTop: "20px",
        textAlign: "right",
        "& button": {
            color: "#306F9D",
            fontSize: "13px",
            textTransform: "inherit",
        },
    },
    box: {
        padding: 0,
        background: "#ffffff",
        paddingTop: "10px",
        paddingBottom: "10px",
        minHeight: "800px",
    },
    innerBox: {
        background: "#ffffff",
        padding: theme.spacing(3),
        marginBottom: theme.spacing(4),
    },
    button: {
        backgroundColor: "#306F9D",
        textTransform: "capitalize",
        color: "white",
        "&:hover": {
            backgroundColor: "#306F9D",
        },
    },
    pagination: {
        "& .MuiTextField-root": {
            width: "35px",
            minWidth: "auto",
        },
        "& .MuiSelect-root": {
            paddingTop: "3px",
            paddingBottom: "3px",
        },
        "& input": {
            padding: "4px 0",
            marginLeft: "10px",
            marginRight: "10px",
        },
        "& button": {
            background: "white",
            color: "#cccccc",
            border: "1px solid #CCCCCC",
            minWidth: "auto",
            width: "24px",
            height: "24px",
            margin: "4px 0",
            "&.left": {
                borderRadius: "4px 0 0 4px",
                marginLeft: "10px",
            },
            "&.right": {
                borderRadius: " 0 4px 4px 0",
            },
        },
    },
}));

const IndeterminateCheckbox = React.forwardRef(
    ({indeterminate, ...rest}, ref) => {
        const defaultRef = React.useRef();
        const resolvedRef = ref || defaultRef;

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return <CustomCheckbox type="checkbox"
                               ref={resolvedRef} {...rest} />;
    }
);

function MemberPlanListingPage(props) {
    const {match, disabled} = props;
    const classes = useStyles();
    const [data, setData] = React.useState([]);
    const [keyword, setKeyword] = React.useState("");
    const [queryString, setQueryString] = React.useState("");
    const {setLoading} = useLoading();

    // for pagination
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [totalElements, setTotalElements] = React.useState(0);

    const CustomLink = React.useMemo(
        () =>
            React.forwardRef((linkProps, ref) => <Link
                ref={ref} {...linkProps} />),
        []
    );

    const getPlans = React.useCallback(async () => {
        setLoading(true);
        try {
           const results = await apiHelper.hkiff.getPlans({keyword: queryString, page, size: rowsPerPage});
           setData(results.data.content);
           setTotalElements(results.data.totalElements)
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    }, [page, queryString, rowsPerPage, setLoading]);

    const deletePlan = async (id) => {
        const decision = await modal.fire({
            title: "Delete Plan",
            text: "Are you sure to delete this plan?",
            showCancelButton: true,
            confirmButtonText: `Delete`,
        });
        if (decision.isConfirmed) {
            try {
                const result = await apiHelper.hkiff.deletePlanById(id);
                if (result.data.code === 0) {
                    toast.dark("Plan deleted.");
                    await getPlans();
                }
            } catch (err) {
                console.log(err);
            }
        }
    };

    const triggerSearch = React.useCallback(() => {
        setQueryString(keyword);
    }, [keyword]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        if (event.target.value > 0) {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        } else {
            getPlans();
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        }
    };

    const columns = React.useMemo(
        () => [
            {
                Header: "Plan Name (English)",
                accessor: "localizations.en.name",
                Cell: ({row}) => {
                    return (
                        <Link
                            to={`${match.url}/${row.original.id}`}
                            className={classes.text}
                        >
                            {row.original.localizations.en.name}
                        </Link>
                    );
                },
            },
            {
                Header: "Plan Name (Traditional)",
                accessor: "localizations.tc.name",
                Cell: ({row}) => {
                    return (
                        <Link
                            to={`${match.url}/${row.original.id}`}
                            className={classes.text}
                        >
                            {row.original.localizations.tc.name}
                        </Link>
                    )
                },
            },
            {
                Header: "Price",
                accessor: "price",
                Cell: ({row}) => {
                    return row.original.price ? `$${row.original.price}` : '-';
                },
            },
            {
                Header: "Status",
                accessor: "status",
                Cell: ({row}) => {
                    switch (row.original.status) {
                        default:
                        case "INACTIVE":
                            return <span
                                className={classes.text}>Inactive</span>
                        case "ACTIVE":
                            return <span
                                className={classes.text}>Active</span>
                    }
                },
            },
        ],
        []
    );

    React.useEffect(() => {
        getPlans();
    }, [getPlans])

    const {
        getTableProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable(
        {
            columns,
            data,
        },
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                // Let's make a column for selection
                {
                    id: "selection",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: ({getToggleAllRowsSelectedProps}) => (
                        <div>
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                        </div>
                    ),
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({row}) => (
                        <div>
                            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                        </div>
                    ),
                },
                ...columns,
                {
                    id: "action",
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: () => <div>Action</div>,
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({row}) => (
                        <div
                            style={{display: "inline-flex"}}>
                            <IconButton
                                className={classes.iconBtn}
                                size="small"
                                component={CustomLink}
                                to={`${match.url}/${row.original.id}`}
                            >
                                <Edit fontSize="small"/>
                            </IconButton>
                            {!disabled && (
                                <IconButton
                                    className={classes.iconBtn}
                                    size="small"
                                    onClick={() => {
                                        deletePlan(row.original.id);
                                    }}
                                >
                                    <Delete
                                        fontSize="inherit"/>
                                </IconButton>
                            )}
                        </div>
                    ),
                },
            ]);
        }
    );


    return (
        <div className={classes.content}>
            <Typography variant="h4" gutterBottom>
                Membership > Plan
            </Typography>
            <Grid
                container
                justify="space-between"
                style={{paddingBottom: "10px"}}
            >
                <Grid container item xs={6} md={6}>
                    <Box
                        width="100%"
                        component="span"
                        display="flex"
                        alignItems="center"
                    >
                    </Box>
                </Grid>
                <Grid container item xs={6} md={6}>
                    <Box
                        width="100%"
                        component="span"
                        display="flex"
                        justifyContent="flex-end"
                        alignItems="center"
                    >
                        <Box display="flex">
                            <Button
                                variant="contained"
                                color="primary"
                                type="button"
                                component={Link}
                                disabled={disabled}
                                to="/hkiff/membership/plan/new"
                            >
                                Add Plan
                            </Button>
                        </Box>
                        <Box display="flex">
                            <Button
                                variant="contained"
                                color="primary"
                                type="button"
                                component={"a"}
                                disabled={disabled}
                                // download
                                // href={`${
                                //     process.env.REACT_APP_API_HOSTNAME ||
                                //     "https://hkiff-api-dev.cloud.udev.hk"
                                // }/membership/admin/members/excelExport${getKeyword(
                                //     queryString
                                // )}`}
                            >
                                Export Plan Data
                            </Button>
                        </Box>
                        <Box display="flex">
                            <TextField
                                id="outlined-error"
                                label="Search Plan"
                                variant="outlined"
                                margin="dense"
                                value={keyword}
                                onChange={(e) => setKeyword(e.target.value)}
                            />
                        </Box>
                        <Box display="flex">
                            <Button
                                variant="outlined"
                                color="primary"
                                type="button"
                                margin="dense"
                                onClick={triggerSearch}
                            >
                                Search
                            </Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
            <Grid container xs={12}>
                <TableContainer
                    component={Paper}
                    style={{
                        boxShadow: "none",
                        border: "1px solid #cccccc",
                    }}
                >
                    <fieldset disabled={disabled}
                              className="fieldset">
                        <Table
                            aria-label="simple table"
                            component="ul"
                            style={{margin: "0"}}
                            {...getTableProps()}
                        >
                            <colgroup>
                                <col
                                    style={{width: "5%"}}/>
                                <col
                                    style={{width: "25%"}}/>
                                <col
                                    style={{width: "25%"}}/>
                                <col
                                    style={{width: "15%"}}/>
                                <col
                                    style={{width: "10%"}}/>
                                <col
                                    style={{width: "5%"}}/>
                            </colgroup>
                            <TableHead component="div">
                                {headerGroups.map((headerGroup) => (
                                    <TableRow {...headerGroup.getHeaderGroupProps()}>
                                        {headerGroup.headers.map((column) => (
                                            <TableCell
                                                {...column.getHeaderProps()}
                                                className={`${column.className ?? ""}`}
                                                style={{
                                                    paddingTop: 6,
                                                    paddingBottom: 6,
                                                    lineHeight: "1.1em",
                                                    height: "48px",
                                                }}
                                            >
                                                {column.render("Header")}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHead>
                            <TableBody>
                                {rows.map((row, index) => {
                                    prepareRow(row);
                                    return (
                                        <TableRow {...row.getRowProps()}>
                                            {row.cells.map((cell) => {
                                                return (
                                                    <TableCell
                                                        {...cell.getCellProps()}
                                                        className={`${cell.column.className ?? ""}`}
                                                        style={{
                                                            paddingTop: 6,
                                                            paddingBottom: 6,
                                                            lineHeight: "1.1em",
                                                            height: "48px",
                                                        }}
                                                    >
                                                        {cell.render("Cell")}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </fieldset>
                </TableContainer>
            </Grid>
            <Grid
                container
                justify="flex-end"
                style={{
                    paddingRight: "25px",
                    marginTop: "15px"
                }}
            >
                <TablePagination
                    component="div"
                    rowsPerPageOptions={[5, 10, 25, { value: 9999999, label: 'All' }]}
                    colSpan={7}
                    count={totalElements}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                        inputProps: {"aria-label": "rows per page"},
                        native: true,
                        style: {
                            background: "white",
                            border: "1px solid #CCCCCC",
                            borderRadius: "4px",
                        },
                    }}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                    labelRowsPerPage="Show rows"
                />
            </Grid>
        </div>
    );
}

export default MemberPlanListingPage;
